/**
 * 選択中の要素のフローティング操作列 (§21・2026-10-19)。右パネル (インスペクタ) を廃止し、要素の設定はすべてキャンバス上で行う。
 *
 * 要素の外接矩形の上 (上に余白が無ければ下) に、動画エディタのシーン操作列と同じアイコン + ツールチップのボタンを並べる:
 * 文言の出どころ (情報・文字の要素のみ) / ロゴのブランド (情報) / 傾き (現在値を併記・ポップオーバーで数値) /
 * 商品画像 (商品のみ・ポップオーバーで何枚目か) / 役割名 (テキストのみ・ポップオーバーで入力) / 複製 / 削除。
 * 回転は要素の上のつまみをドラッグでも (BannerLayoutCanvas)。
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Copy, Image as ImageIcon, Info, RotateCw, Tag, Trash2 } from 'lucide-react';
import { Popover } from '../../../features/videoEditor/components/Popover';
import { Tooltip } from '../../common/Tooltip';
import { rotatedBounds, withRotation } from '../../../features/bannerLayout/geometry';
import { BANNER_LAYOUT_ROLES } from '../../../features/bannerLayout/roles';
import { LAYOUT_TEXT_FIELD_LABELS } from '../../../features/bannerLayout/textSync';
import { BANNER_LAYOUT_MAX_LABEL, BANNER_LAYOUT_WARN_TEXT, type BannerLayoutElement } from '../../../features/bannerLayout/types';
import { useEscapeCapture } from '../../../features/bannerLayout/useEscapeCapture';
import { ProductImagePicker, RotationControl, type InspectorAsset } from './BannerLayoutControls';
import styles from './BannerLayout.module.css';

/** 操作列の高さ + 要素との間隔 (px)。上に置けないときは下に回す (下は回転つまみがあるので、その分さらに離す) */
const TOOLBAR_OFFSET = 38;
const TOOLBAR_BELOW_GAP = 36;

type PopoverKind = 'rotation' | 'image' | 'label';

export function BannerLayoutElementToolbar({
  element,
  label,
  canvasWidth,
  canvasHeight,
  assets,
  logoBrandNames,
  excludeText,
  firstOfKind,
  assignedText,
  onChange,
  onDuplicate,
  onDelete,
}: {
  element: BannerLayoutElement;
  /** 番号付きの役割名 (elementLabels) */
  label: string;
  canvasWidth: number;
  canvasHeight: number;
  assets: InspectorAsset[];
  logoBrandNames: string[];
  excludeText: boolean;
  /** この枠が同じ種類の最初の枠か (= 「テキスト」欄の値が入る枠か) */
  firstOfKind: boolean;
  /** この枠に入る文言 (欄が空・2個目以降なら空文字) */
  assignedText: string;
  onChange: (next: BannerLayoutElement) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<PopoverKind | null>(null);
  const close = useCallback(() => setOpen(null), []);
  useEscapeCapture(open !== null, close);
  const toggle = (kind: PopoverKind) => setOpen((v) => (v === kind ? null : kind));

  const role = BANNER_LAYOUT_ROLES[element.kind];
  // 傾いた要素は外接矩形の上に出す
  const bounds = rotatedBounds(element, canvasWidth / canvasHeight);
  const topPx = bounds.y * canvasHeight;
  const bottomPx = (bounds.y + bounds.h) * canvasHeight;
  const centerPx = (bounds.x + bounds.w / 2) * canvasWidth;
  const above = topPx >= TOOLBAR_OFFSET;
  const style = { left: centerPx, top: above ? topPx - TOOLBAR_OFFSET : bottomPx + TOOLBAR_BELOW_GAP };

  const textSource: ReactNode = !role.hasText ? null : excludeText ? (
    '「テキストを含めない」がオンのため、文字の要素は生成時に使われません'
  ) : firstOfKind ? (
    <>
      「テキスト」の「{LAYOUT_TEXT_FIELD_LABELS[element.kind]}」欄の文言が入ります。
      {assignedText ? <><br />{assignedText}</> : '欄が空のため、AIが文言を考えます。'}
      {assignedText.length > BANNER_LAYOUT_WARN_TEXT && <><br />長い文言は崩れやすくなります</>}
    </>
  ) : (
    '同じ種類の2つ目以降の枠です。文言はAIが考えます。'
  );
  const rotation = element.rotation ?? 0;

  return (
    <div
      ref={anchorRef}
      className={styles.elementToolbar}
      style={style}
      data-testid="banner-layout-toolbar"
      // キャンバスの「空白クリックで選択解除」とドラッグ開始を止める
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <span className={styles.elementToolbarLabel} style={{ color: role.color }}>{label}</span>
      {textSource && (
        <Tooltip content={textSource} placement="bottom" disabled={open !== null}>
          <span className={styles.elementToolbarInfo} role="img" aria-label="文言の出どころ" data-testid="banner-layout-text-source">
            <Info size={14} aria-hidden="true" />
          </span>
        </Tooltip>
      )}
      {element.kind === 'logo' && logoBrandNames.length > 0 && (
        <Tooltip content={`ブランド「${logoBrandNames.join('、')}」のロゴを入れます`} placement="bottom" disabled={open !== null}>
          <span className={styles.elementToolbarInfo} role="img" aria-label="ロゴのブランド">
            <Info size={14} aria-hidden="true" />
          </span>
        </Tooltip>
      )}
      <Tooltip content="傾き (要素の上のつまみをドラッグでも回せます)" placement="bottom" disabled={open !== null}>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.elementToolbarButton} ${styles.elementToolbarRotate} ${open === 'rotation' || rotation ? styles.iconButtonActive : ''}`}
          aria-label={`傾き: ${rotation}度`}
          aria-haspopup="dialog"
          aria-expanded={open === 'rotation'}
          onClick={() => toggle('rotation')}
          data-testid="banner-layout-rotation-button"
        >
          <RotateCw size={14} aria-hidden="true" />
          <span className={styles.elementToolbarValue}>{rotation}°</span>
        </button>
      </Tooltip>
      {element.kind === 'product' && (
        <Tooltip content="商品画像 (選択中の画像の何枚目か)" placement="bottom" disabled={open !== null}>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.elementToolbarButton} ${open === 'image' ? styles.iconButtonActive : ''}`}
            aria-label="商品画像"
            aria-haspopup="dialog"
            aria-expanded={open === 'image'}
            onClick={() => toggle('image')}
            data-testid="banner-layout-product-image-button"
          >
            <ImageIcon size={14} aria-hidden="true" />
          </button>
        </Tooltip>
      )}
      {element.kind === 'text' && (
        <Tooltip content="役割名 (この枠が何の文字かをAIに伝える名前。例: サブコピー / 特典)" placement="bottom" disabled={open !== null}>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.elementToolbarButton} ${open === 'label' ? styles.iconButtonActive : ''}`}
            aria-label="役割名を変更"
            aria-haspopup="dialog"
            aria-expanded={open === 'label'}
            onClick={() => toggle('label')}
            data-testid="banner-layout-label-button"
          >
            <Tag size={14} aria-hidden="true" />
          </button>
        </Tooltip>
      )}
      <Tooltip content="複製 (Cmd/Ctrl+D)" placement="bottom" disabled={open !== null}>
        <button type="button" className={`${styles.iconButton} ${styles.elementToolbarButton}`} aria-label="複製" onClick={onDuplicate}>
          <Copy size={14} aria-hidden="true" />
        </button>
      </Tooltip>
      <Tooltip content="削除 (Delete)" placement="bottom" disabled={open !== null}>
        <button type="button" className={`${styles.iconButton} ${styles.elementToolbarButton} ${styles.elementToolbarDanger}`} aria-label="削除" onClick={onDelete}>
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </Tooltip>

      <Popover anchorRef={anchorRef} open={open === 'rotation'} onClose={close} className={styles.elementPopover} role="dialog" ariaLabel="傾き" inModal>
        <RotationControl value={rotation} onChange={(deg) => onChange(withRotation(element, deg))} />
      </Popover>
      {element.kind === 'product' && (
        <Popover anchorRef={anchorRef} open={open === 'image'} onClose={close} className={styles.elementPopover} role="dialog" ariaLabel="商品画像" inModal>
          <ProductImagePicker element={element} assets={assets} onChange={onChange} />
        </Popover>
      )}
      {element.kind === 'text' && (
        <Popover anchorRef={anchorRef} open={open === 'label'} onClose={close} className={styles.elementPopover} role="dialog" ariaLabel="役割名" inModal>
          <label className={styles.field}>
            <span>役割名</span>
            <input
              type="text"
              className={styles.input}
              maxLength={BANNER_LAYOUT_MAX_LABEL}
              value={element.label ?? ''}
              placeholder="テキスト (例: サブコピー / 特典)"
              autoFocus
              onChange={(e) => onChange({ ...element, label: e.target.value })}
              aria-label="役割名"
            />
            <span className={styles.inspectorHint}>ガイドの枠とプロンプトにこの名前が入ります (空なら「テキスト」)</span>
          </label>
        </Popover>
      )}
    </div>
  );
}
